import { useState, type ReactNode } from 'react';
import { ExportModal } from '@components/ExportModal';
import { BarraResultados } from '@components/FiltrosReporte';
import { Paginador } from '@components/Paginador';
import { useReportePaginado } from '@lib/hooks/useReportePaginado';
import { paramsExport } from '@lib/services/exportaciones.service';

/**
 * Pantalla de un reporte paginado con descarga (Reporte ABM Web, Reporte de
 * padrón): encabezado, filtros, total, tabla, paginador y el modal del export.
 * Cada reporte pone sus filtros y su tabla; el resto se comporta igual en los
 * dos, que es lo que se espera de dos pantallas que se usan una al lado de la
 * otra.
 *
 * Va paginado: sin filtros el universo son decenas de miles de filas. La
 * descarga NO baja la página que se está viendo sino el filtro completo, y por
 * eso va por el export async (job + CSV→GridFS + xlsx en el cliente), con
 * `clave` como tipo de export.
 */
export function PantallaReporte<P extends object, R>({
  titulo,
  descripcion,
  icono,
  clave,
  obtener,
  pageSize,
  filtros: renderFiltros,
  tabla: renderTabla,
}: {
  titulo: string;
  descripcion: string;
  icono: ReactNode;
  /** `queryKey` de la vista y tipo del export (`/exportaciones/:tipo`). */
  clave: string;
  obtener: (filtros: P, page: number, pageSize: number) => Promise<{ items: R[]; total: number }>;
  pageSize: number;
  filtros: (buscar: (params: P) => void, loading: boolean) => ReactNode;
  tabla: (rows: R[], loading: boolean) => ReactNode;
}) {
  const { filtros, page, setPage, query, rows, total, totalPaginas, buscar } = useReportePaginado(
    clave,
    obtener,
    pageSize,
  );
  /** Los filtros con los que se abrió el modal de descarga, congelados. Si se
   *  tomaran de `filtros`, cambiar la búsqueda con el modal abierto le movería
   *  los params por debajo: el job que ya está corriendo es el de los filtros
   *  viejos, pero el modal pasaría a esperar el de los nuevos y se quedaría
   *  colgado hasta el deadline mostrando un error que no es. */
  const [exportParams, setExportParams] = useState<Record<string, string> | null>(null);

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {icono}
          {titulo}
        </h1>
        <p className="text-element-mid">{descripcion}</p>
      </header>

      {renderFiltros(buscar, query.isFetching)}

      {query.isError && (
        <p className="status status-error" style={{ marginBottom: 12 }}>
          No se pudo generar el reporte. Intente nuevamente.
        </p>
      )}

      {filtros && query.data && (
        <BarraResultados total={total} onDescargar={() => setExportParams(paramsExport(filtros))} />
      )}

      {renderTabla(rows, query.isFetching && filtros !== null && !query.data)}

      {total > pageSize && (
        <Paginador
          page={page}
          totalPaginas={totalPaginas}
          disabled={query.isFetching}
          onChange={setPage}
        />
      )}

      {exportParams && (
        <ExportModal
          tipo={clave}
          params={exportParams}
          titulo={titulo}
          onClose={() => setExportParams(null)}
        />
      )}
    </section>
  );
}
